'use client';

import { Calendar, Dumbbell, Users } from 'lucide-react';
import {
  ConfirmModal,
  OutcomeList,
  StatusIcon,
  type OutcomeItem,
} from '@mokoboko/components';
import { ERROR_STATUS_ICON_CLASS } from '@/lib/error-ramp';

import {
  classWithInstructorText,
  formatClassDateTime,
  type TimeFormat,
} from '@/lib/utils';
import { useTimeFormat } from '@/state/tenant';
import type { BookingWarning } from '@/hooks/use-booking-guard';
import type { BookingBlockReason } from '@/services/order';

type ClassFacts = {
  classTitle: string;
  instructorName: string;
  startTime: string;
  timezone?: string;
};

const spotsLabel = (spots: number) => `${spots} spot${spots === 1 ? '' : 's'}`;

export const buildFacts = (
  { classTitle, instructorName, startTime, timezone }: ClassFacts,
  timeFormat: TimeFormat,
  spotsText: string | null
): OutcomeItem[] => [
  {
    icon: <Dumbbell />,
    text: classWithInstructorText(classTitle, instructorName),
  },
  {
    icon: <Calendar />,
    text: formatClassDateTime(startTime, timeFormat, timezone, 'long'),
  },
  ...(spotsText ? [{ icon: <Users />, text: spotsText }] : []),
];

const BOOK_ANOTHER_TIME = 'Would you like to book another time?';
const BOOK_ANOTHER_CLASS = 'You can book another class instead.';
const CLOSED_FALLBACK_TITLE = 'Bookings for this class are closed';

// The reason list lives on the backend and grows there, so an unknown one must
// render the message it already ships rather than be looked up and missed.
const CLOSED_TITLE_OVERRIDES: Partial<Record<BookingBlockReason, string>> = {
  CLASS_ALREADY_STARTED: 'This class has already started',
};

// The backend message for a started class only repeats the title; every other
// reason carries the actual rule, so it stays.
const REASONS_WITHOUT_MESSAGE: BookingBlockReason[] = ['CLASS_ALREADY_STARTED'];

// A one-time class has no other time, so it points at the other classes.
const describeClosed = (
  reason: BookingBlockReason,
  message: string | null | undefined,
  isOneTime: boolean
) => {
  const next = isOneTime ? BOOK_ANOTHER_CLASS : BOOK_ANOTHER_TIME;
  const sentence = REASONS_WITHOUT_MESSAGE.includes(reason)
    ? undefined
    : message?.trim();
  if (!sentence) return next;
  return /[.!?]$/.test(sentence)
    ? `${sentence} ${next}`
    : `${sentence}. ${next}`;
};

type BookingWarningModalProps = ClassFacts & {
  warning: BookingWarning<unknown>;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  isOneTime?: boolean;
};

export const BookingWarningModal = ({
  warning,
  isOpen,
  onOpenChange,
  onConfirm,
  isOneTime = false,
  ...classFacts
}: BookingWarningModalProps) => {
  const timeFormat = useTimeFormat();
  return warning.kind === 'rebook' ? (
    <ConfirmModal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      icon={<StatusIcon variant="warning" size="md" />}
      title="You already booked this class"
      description="Do you want to book it again?"
      body={
        <OutcomeList
          items={buildFacts(
            classFacts,
            timeFormat,
            `${spotsLabel(warning.spots)} booked`
          )}
        />
      }
      onConfirm={onConfirm}
      cancelLabel="Go back"
      confirmLabel="Yes, continue"
    />
  ) : (
    <ConfirmModal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      icon={
        <StatusIcon
          variant="error"
          size="xl"
          className={ERROR_STATUS_ICON_CLASS}
        />
      }
      title={CLOSED_TITLE_OVERRIDES[warning.reason] ?? CLOSED_FALLBACK_TITLE}
      description={describeClosed(warning.reason, warning.message, isOneTime)}
      body={
        <OutcomeList
          items={buildFacts(
            classFacts,
            timeFormat,
            warning.spots > 0
              ? `You already booked ${spotsLabel(warning.spots)}`
              : null
          )}
        />
      }
      onConfirm={onConfirm}
      cancelLabel="Close"
      confirmLabel={isOneTime ? 'See all classes' : 'See other times'}
    />
  );
};
